import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Calendar,
  CheckboxOptionType,
  Flex,
  Form,
  Input,
  Radio,
  Spin,
  Tooltip,
} from 'antd';
import useOngoingOrder from '../../../hooks/context/useOngoingOrder';
import { Address } from '../../../types/order';
import { usePlacesWidget } from 'react-google-autocomplete';
import classnames from 'classnames';
import useGoogleMaps from '../../../hooks/utility/useGoogleMaps';
import { ScheduledOrderSlot } from '../../../types/availability';
import useOrderMethodAvailability from '../../../hooks/availability/useOrderMethodAvailability';
import {
  loadDisabledPreOrderDates,
  loadScheduledOrderSlots,
} from '../../../services/api/AvailabilityService';
import useStore from '../../../hooks/context/useStore';
import collect, { Collection } from 'collect.js';
import Section from '../../../components/pages/order-flow/order-info/Section';
import HourSlots from '../../../components/pages/order-flow/order-info/HourSlots';
import { LeftOutlined } from '@ant-design/icons';
import { createDeliveryQuote, findDeliveryZone } from '../../../services/api/DeliveryService';
import useData from '../../../hooks/utility/useData';
import { AddressHelpers } from '../../../services/utils/address/AddressHelpers';
import { DeliveryMode, OrderMethod, OrderScheduleOption } from '../../../types/constants';
import AddressFormatter from '../../../services/utils/address/AddressFormatter';
import { useNavigate } from 'react-router';
import { Routes } from '../../../routing/Router';
import moment from 'moment-timezone';
import { Moment } from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import OrderFlowStepper from '../../../components/pages/order-flow/OrderFlowStepper';
import { BaseComponentProps } from '../../../types';
import StoreDeliveryInfo from '../../../components/store/StoreDeliveryInfo';
import { DeliveryZoneIdentifier } from '../../../components/store/DeliveryZone';
import useHelpers from '../../../hooks/utility/useHelpers';
import { Stringable } from '../../../services/utils/Stringable';
import { IDeliveryZone } from '../../../types/delivery';

enum AddressServiceability {
  Unknown = 'unknown',
  Serviceable = 'serviceable',
  NonServiceable = 'non_serviceable',
}

type AddressValidation = {
  loading: boolean;
  serviceability: AddressServiceability;
  address: Pick<Address, 'street_name' | 'street_number' | 'city' | 'zip_code'>;
};

export default function OrderInfoPage() {
  const { orderData, scheduledTime, updateOrderData, isExternalDelivery } = useOngoingOrder();

  const { storeId, storeData } = useStore();
  const { extractAddressComponents, matchPlaceAddress, findCoordinates } = useGoogleMaps();
  const { isOrderMethodCurrentlyAvailable, isOrderMethodAvailableForFutureOrders } =
    useOrderMethodAvailability();
  const navigate = useNavigate();
  const { formatCurrency } = useHelpers();

  const orderMethod = orderData.method ?? OrderMethod.Pickup;

  const isAddressComplete = useMemo(
    () =>
      Boolean(orderData.delivery_info.street_name) &&
      Boolean(orderData.delivery_info.street_number) &&
      Boolean(orderData.delivery_info.city) &&
      Boolean(orderData.delivery_info.zip_code),
    [orderData.delivery_info],
  );

  const [showAddressFields, setShowAddressFields] = useState(isAddressComplete);
  const [addressSearch] = useState(AddressFormatter.format(orderData.delivery_info));
  const [googlePlace, setGooglePlace] = useState(null);

  const { data: addressValidation, updateData: updateAddressValidation } =
    useData<AddressValidation>({
      loading: false,
      serviceability: orderData.delivery_info.zone
        ? AddressServiceability.Serviceable
        : AddressServiceability.Unknown,
      address: orderData.delivery_info,
    });

  const isAddressValidationMatching = useMemo(
    () => AddressHelpers.match(orderData.delivery_info, addressValidation.address),
    [orderData.delivery_info, addressValidation.address],
  );

  const [selectedDate, setSelectedDate] = useState<Moment>(null);
  const [disabledDates, setDisabledDates] = useState<number[]>(null);
  const [slots, setSlots] = useState<ScheduledOrderSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const groupedSlots = useMemo<Collection<ScheduledOrderSlot[]>>(
    () =>
      collect(slots).mapToGroups((item: ScheduledOrderSlot) => [
        moment(item.start_time).hour(),
        item,
      ]),
    [slots],
  );

  const isDateDisabled = useCallback(
    (date: Moment): boolean =>
      date.isBefore(moment(), 'day') ||
      date.isAfter(moment().add(storeData.order_settings.max_pre_order_days, 'days'), 'day') ||
      collect(disabledDates).contains(date.date()),
    [storeData.order_settings.max_pre_order_days, disabledDates],
  );

  const isInputValid = useMemo(
    () =>
      Boolean(orderData.method) &&
      Boolean(orderData.schedule_info.option) &&
      (orderData.schedule_info.option === OrderScheduleOption.Asap ||
        orderData.schedule_info.scheduled_for) &&
      (orderData.method === OrderMethod.Pickup ||
        (isAddressValidationMatching &&
          addressValidation.serviceability === AddressServiceability.Serviceable)),
    [
      orderData.method,
      orderData.schedule_info,
      addressValidation.serviceability,
      isAddressValidationMatching,
    ],
  );

  useEffect(() => {
    const date = moment(selectedDate ?? undefined);

    loadDisabledDates(date).then(() => loadSlots(date));
  }, [orderMethod, orderData.schedule_info.option]);

  async function loadDisabledDates(currentMonth: Moment) {
    loadDisabledPreOrderDates(storeId, {
      order_method: orderMethod,
      month: currentMonth.month() + 1,
      year: currentMonth.year(),
    })
      .then(({ data }) => setDisabledDates(data))
      .catch((e) => console.error('Failed to load disabled pre order dates', { e }));
  }

  async function loadSlots(currentDate: Moment) {
    if (
      !orderData.schedule_info.option ||
      orderData.schedule_info.option === OrderScheduleOption.Asap
    ) {
      return;
    }

    setLoadingSlots(true);

    const date =
      isOrderMethodAvailableForFutureOrders(orderMethod) || currentDate.isSame(moment(), 'day')
        ? currentDate
        : moment();

    loadScheduledOrderSlots(storeId, {
      order_method: orderMethod,
      date: date.format('DD-MM-YYYY'),
    })
      .then(({ data }) => setSlots(data))
      .catch((e) => console.error('Failed to load scheduled order slots', { e }))
      .finally(() => setLoadingSlots(false));
  }

  const loadAddressCoordinates = async (): Promise<Address> => {
    if (googlePlace && matchPlaceAddress(googlePlace, orderData.delivery_info)) {
      return orderData.delivery_info;
    }

    return await findCoordinates(orderData.delivery_info);
  };

  async function validateDeliveryDetails() {
    updateAddressValidation({
      loading: true,
      serviceability: AddressServiceability.Unknown,
    });

    const address = await loadAddressCoordinates();

    if (!address.lat || !address.lng) {
      return updateAddressValidation({
        loading: false,
        serviceability: AddressServiceability.NonServiceable,
        address,
      });
    }

    updateAddressValidation('address', address);
    updateOrderData('delivery_info', {
      ...orderData.delivery_info,
      ...address,
    });

    return storeData.order_settings.delivery_mode === DeliveryMode.Internal
      ? loadDeliveryZone(address)
      : loadDeliveryQuote(address);
  }

  async function loadDeliveryQuote(address: Address) {
    return createDeliveryQuote(storeId, {
      ...address,
      dropoff_time: scheduledTime,
    })
      .then(({ data }) => updateOrderData('delivery_info.quote', data))
      .then(() => loadDeliveryZone(address))
      .catch(() => updateAddressValidation('serviceability', AddressServiceability.NonServiceable))
      .finally(() => updateAddressValidation('loading', false));
  }

  async function loadDeliveryZone(address: Address) {
    return findDeliveryZone(storeId, address)
      .then(({ data }) => updateOrderData('delivery_info.zone', data))
      .then(() => updateAddressValidation('serviceability', AddressServiceability.Serviceable))
      .catch(() => updateAddressValidation('serviceability', AddressServiceability.NonServiceable))
      .finally(() => updateAddressValidation('loading', false));
  }

  const onPlaceSelected = (place: google.maps.places.PlaceResult) => {
    setGooglePlace(place);
    setShowAddressFields(true);
    updateOrderData('delivery_info', {
      ...extractAddressComponents(place),
      note: orderData.delivery_info.note,
      doorbell_name: orderData.delivery_info.doorbell_name,
      zone: null,
      quote: null,
    });
  };

  const onScheduleOptionSelected = (value: OrderScheduleOption) => {
    updateOrderData('schedule_info.option', value);
    !selectedDate && setSelectedDate(moment());
  };

  const onDateSelected = async (value: Moment) => {
    if (value.month() !== selectedDate?.month()) {
      await loadDisabledDates(value);
    }

    loadSlots(value);
    setSelectedDate(value);
  };

  const placesInputRef = useRef(null);
  const { ref: placesWidgetRef } = usePlacesWidget<HTMLInputElement>({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    onPlaceSelected,
    options: {
      fields: ['name', 'formatted_address', 'address_components', 'geometry'],
      types: [],
      componentRestrictions: {
        country: 'DE',
      },
    },
  });

  const timeOptions = useMemo<CheckboxOptionType<OrderScheduleOption>[]>(
    () =>
      collect()
        .when(
          storeData.availability.open_now && storeData.order_settings.has_asap_orders,
          (collection) => collection.push({ value: OrderScheduleOption.Asap, label: 'ASAP' }),
        )
        .when(storeData.order_settings.has_scheduled_orders, (collection) =>
          collection.push({ value: OrderScheduleOption.Scheduled, label: 'Scheduled' }),
        )
        .toArray(),
    [
      storeData.availability.open_now,
      storeData.order_settings.has_asap_orders,
      storeData.order_settings.has_scheduled_orders,
    ],
  );

  const orderMethodOptions = useMemo<CheckboxOptionType<OrderMethod>[]>(() => {
    const isOrderMethodDisabled = (item: OrderMethod): boolean => {
      if (!orderData.schedule_info.option) {
        return (
          !isOrderMethodCurrentlyAvailable(item) || !isOrderMethodAvailableForFutureOrders(item)
        );
      }

      return orderData.schedule_info.option === OrderScheduleOption.Asap
        ? !isOrderMethodCurrentlyAvailable(item)
        : !isOrderMethodAvailableForFutureOrders(item);
    };

    return [OrderMethod.Pickup, OrderMethod.Delivery].map((item) => ({
      value: item,
      label: Stringable.toHuman(item),
      disabled: isOrderMethodDisabled(item),
    }));
  }, [
    orderData.schedule_info.option,
    isOrderMethodCurrentlyAvailable,
    isOrderMethodAvailableForFutureOrders,
  ]);

  const getDeliveryFee = useCallback(
    (deliveryZone: IDeliveryZone) => {
      if (!isExternalDelivery) {
        return deliveryZone.fee;
      }

      return orderData.delivery_info.quote?.customer_price;
    },
    [isExternalDelivery, orderData.delivery_info.quote],
  );

  return (
    <>
      <Flex justify="space-between">
        <Button
          type="text"
          onClick={() => navigate(`/${Routes.NewOrder.Root}/${Routes.NewOrder.OrderItems}`)}
          icon={<LeftOutlined />}
          className="my-auto"
        >
          Back to menu
        </Button>
      </Flex>
      <OrderFlowStepper />
      <Flex justify="space-between">
        <h1 className="mt-sm">Order info</h1>
        <Button
          type="primary"
          disabled={!isInputValid}
          onClick={() => navigate(`/${Routes.NewOrder.Root}/${Routes.NewOrder.OrderConfirmation}`)}
          size="large"
          className="my-auto"
        >
          Proceed to order confirmation
        </Button>
      </Flex>
      <div className="mt-lg">
        <Form layout="vertical">
          <Section
            divider
            summary={
              <div className="w-1/4 xl:w-1/5">
                <h6 className="mb-sm">Ready time</h6>
                <Form.Item className="mb-none">
                  <Radio.Group
                    value={orderData.schedule_info.option}
                    onChange={(e) => onScheduleOptionSelected(e.target.value)}
                    options={timeOptions}
                    className="flex flex-col gap-xs"
                  />
                </Form.Item>
                {orderData.schedule_info.option === OrderScheduleOption.Scheduled && (
                  <Form.Item className="mb-none">
                    <Calendar
                      value={selectedDate ? dayjs.unix(selectedDate.unix()) : null}
                      onChange={(value: Dayjs) => onDateSelected(moment.unix(value.unix()))}
                      fullscreen={false}
                      disabledDate={(date: Dayjs) => isDateDisabled(moment.unix(date.unix()))}
                    />
                  </Form.Item>
                )}
              </div>
            }
          >
            {orderData.schedule_info.option === OrderScheduleOption.Asap ? null : (
              <div className="w-full overflow-x-auto">
                {selectedDate && (
                  <h5 className="underline">{selectedDate.format('dddd, DD MMMM')}</h5>
                )}
                <div className="flex mt-lg gap-lg">
                  {loadingSlots ? (
                    <div className="w-full h-full flex gap-sm">
                      <Spin size="large" className="m-auto" />
                    </div>
                  ) : (
                    <>
                      {selectedDate &&
                        !isDateDisabled(selectedDate) &&
                        groupedSlots
                          .keys()
                          .map((hour) => (
                            <HourSlots
                              hour={Number.parseInt(hour)}
                              slots={groupedSlots.get(hour)}
                              key={`hour-${hour}-slots`}
                            />
                          ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </Section>
          <Section
            summary={
              <div className="w-1/4 xl:w-1/5">
                <h6 className="mb-sm">Order method</h6>
                <Form.Item>
                  <Radio.Group
                    value={orderData.method}
                    onChange={(e) => updateOrderData('method', e.target.value)}
                    className="flex flex-col gap-xs"
                  >
                    {orderMethodOptions.map((item) => {
                      const Wrapper = ({ children }: BaseComponentProps) =>
                        item.disabled ? (
                          <Tooltip
                            title={`${item.label} is currently not available`}
                            trigger="hover"
                            placement="right"
                          >
                            {children}
                          </Tooltip>
                        ) : (
                          children
                        );

                      return (
                        <Wrapper key={item.value}>
                          <Radio value={item.value} disabled={item.disabled}>
                            {item.label}
                          </Radio>
                        </Wrapper>
                      );
                    })}
                  </Radio.Group>
                </Form.Item>
              </div>
            }
          >
            <div className="w-full">
              {isAddressValidationMatching &&
                addressValidation.serviceability !== AddressServiceability.Unknown && (
                  <Alert
                    message={
                      addressValidation.serviceability === AddressServiceability.Serviceable
                        ? `Delivery address is supported!`
                        : 'Delivery address is not supported!'
                    }
                    description={
                      addressValidation.serviceability === AddressServiceability.Serviceable && (
                        <span className="text-md font-semibold underline">
                          Price - ${formatCurrency(getDeliveryFee(orderData.delivery_info.zone))}
                        </span>
                      )
                    }
                    type={
                      addressValidation.serviceability === AddressServiceability.Serviceable
                        ? 'success'
                        : 'error'
                    }
                    className="mb-sm"
                  />
                )}
              <div
                className={classnames('w-full grid grid-cols-2 gap-sm', {
                  hidden: orderData.method !== OrderMethod.Delivery,
                })}
              >
                <h5 className="underline col-span-2">Delivery info</h5>
                <div className="col-span-2 mt-sm flex gap-md">
                  <StoreDeliveryInfo data={storeData} className="min-h-80 h-full" />
                  <div className="overflow-y-auto flex-none">
                    <h4 className="mb-md">Delivery cost</h4>
                    {storeData.order_settings.delivery_zones.map((item, index) => {
                      const deliveryFee = getDeliveryFee(item);

                      return (
                        <div
                          className={classnames('py-xs', { 'border-t': index > 0 })}
                          key={`delivery-zone-${item.id}-delivery-costs`}
                        >
                          <div className="flex gap-xs">
                            <DeliveryZoneIdentifier data={item} />
                            <h6 className="text-sm my-auto">{item.name}</h6>
                          </div>
                          <ul className="text-sm mt-xs list-disc list-inside">
                            <li>Min. amount - {formatCurrency(item.min_threshold)}</li>
                            {Boolean(deliveryFee) && (
                              <li>Fee - {formatCurrency(getDeliveryFee(item))}</li>
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <Form.Item
                  name="address"
                  label="Search address"
                  initialValue={addressSearch}
                  className="col-span-2 mb-none"
                >
                  <Input.Search
                    ref={(c) => {
                      placesInputRef.current = c;

                      if (c) {
                        c.input.autocomplete = 'off';
                        placesWidgetRef.current = c.input;
                      }
                    }}
                  />
                </Form.Item>
                {showAddressFields && (
                  <>
                    <Form.Item label="Street" className="mb-none">
                      <Input
                        value={orderData.delivery_info.street_name}
                        onChange={(e) =>
                          updateOrderData('delivery_info.street_name', e.target.value)
                        }
                      />
                    </Form.Item>
                    <Form.Item label="Number" className="mb-none">
                      <Input
                        value={orderData.delivery_info.street_number}
                        onChange={(e) =>
                          updateOrderData('delivery_info.street_number', e.target.value)
                        }
                      />
                    </Form.Item>
                    <Form.Item label="City" className="mb-none">
                      <Input
                        value={orderData.delivery_info.city}
                        onChange={(e) => updateOrderData('delivery_info.city', e.target.value)}
                      />
                    </Form.Item>
                    <Form.Item label="Zip code" className="mb-none">
                      <Input
                        value={orderData.delivery_info.zip_code}
                        onChange={(e) => updateOrderData('delivery_info.zip_code', e.target.value)}
                      />
                    </Form.Item>
                  </>
                )}
              </div>
              {orderData.method === OrderMethod.Delivery && showAddressFields && (
                <Button
                  loading={addressValidation.loading}
                  disabled={!isAddressComplete}
                  onClick={validateDeliveryDetails}
                  type="primary"
                  size="large"
                  className="mt-md"
                >
                  Validate address
                </Button>
              )}
            </div>
          </Section>
        </Form>
      </div>
    </>
  );
}
